import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

/**
 * Forced first-login password change page.
 *
 * Shown when the signed-in user has `must_change_password === true` (for
 * example, customers who were auto-provisioned by a Verify-Email campaign
 * and signed in with the temporary password from their welcome email).
 *
 * The user must type their CURRENT (temporary) password plus a new password
 * twice before they can reach any other route. Backend `must_change_password`
 * is cleared in the same `change-password` call so the next /auth/me refresh
 * lets them proceed normally.
 */
export default function SetPassword() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!current || !next || !confirm) {
      toast.error("Fill in all three fields");
      return;
    }
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next === current) {
      toast.error("Pick a password different from your temporary one");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated — welcome!");
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4" data-testid="set-password-page">
      <Toaster position="top-right" />
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-2xl shadow-sm p-8">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 border border-blue-100 mx-auto mb-4">
          <ShieldCheck size={22} weight="duotone" className="text-[#002BF6]" />
        </div>
        <h1
          className="text-xl font-semibold text-zinc-900 text-center"
          style={{ fontFamily: "Work Sans" }}
        >
          Set your password
        </h1>
        <p className="text-sm text-zinc-600 text-center mt-2 leading-relaxed">
          Hi {user?.name || user?.email}, please choose a permanent password before continuing. Use the temporary
          password from your welcome email as your current password.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
              Current (temporary) password
            </Label>
            <div className="relative mt-1">
              <Input
                type={show ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoFocus
                data-testid="set-password-current"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                data-testid="set-password-toggle-visibility"
              >
                {show ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
              New password
            </Label>
            <Input
              type={show ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              minLength={8}
              data-testid="set-password-new"
              className="mt-1"
            />
            <div className="text-[10px] text-zinc-500 mt-1">
              Minimum 8 characters. Use letters, numbers, and symbols for a stronger password.
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
              Confirm new password
            </Label>
            <Input
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={8}
              data-testid="set-password-confirm"
              className="mt-1"
            />
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-[#002BF6] hover:bg-[#0021C7] text-white"
            data-testid="set-password-submit"
          >
            {busy ? "Updating..." : "Save password & continue"}
          </Button>
        </form>

        <button
          type="button"
          onClick={async () => {
            await logout();
            navigate("/login", { replace: true });
          }}
          className="mt-4 w-full text-xs text-zinc-500 hover:text-zinc-800 hover:underline"
          data-testid="set-password-signout"
        >
          Sign out and finish later
        </button>
      </div>
    </div>
  );
}
